import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { GridPattern } from './DecorativeElements';
import './PartnersSection.css';

const PartnersSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.01,
    triggerOnce: true,
    rootMargin: '300px', // Start loading 300px before section enters viewport
  });

  // Generate array of all partner images (1-53)
  const allPartners = Array.from({ length: 53 }, (_, i) => {
    const imageNumber = i + 1;
    return {
      id: imageNumber,
      logo: `/assets/img/partners/${imageNumber}.png`,
    };
  });

  // Split logos into 3 rows (18, 18, 17 logos)
  const row1Partners = allPartners.slice(0, 18);
  const row2Partners = allPartners.slice(18, 36);
  const row3Partners = allPartners.slice(36, 53);

  // Duplicate each row for seamless loop
  const duplicatedRow1 = [...row1Partners, ...row1Partners];
  const duplicatedRow2 = [...row2Partners, ...row2Partners];
  const duplicatedRow3 = [...row3Partners, ...row3Partners];

  // Preload images immediately when component mounts (aggressive preloading)
  useEffect(() => {
    // Preload first few logos from each row immediately with high priority
    const priorityLogos = [
      ...row1Partners.slice(0, 9),
      ...row2Partners.slice(0, 9),
      ...row3Partners.slice(0, 9)
    ].map(p => p.logo);
    
    // Preload priority logos immediately
    priorityLogos.forEach((logo, index) => {
      const img = new Image();
      img.fetchPriority = index < 9 ? 'high' : 'auto';
      img.loading = index < 9 ? 'eager' : 'lazy';
      img.src = logo;
    });

    // Load remaining logos progressively
    const remainingLogos = [
      ...row1Partners.slice(9),
      ...row2Partners.slice(9),
      ...row3Partners.slice(9)
    ];

    remainingLogos.forEach((partner, index) => {
      setTimeout(() => {
        const img = new Image();
        img.loading = 'lazy';
        img.src = partner.logo;
      }, index * 20);
    });
  }, []);

  const placeholderSvg = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='80'%3E%3Crect width='150' height='80' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle' font-family='Arial' font-size='12' fill='%23999'%3ELogo%3C/text%3E%3C/svg%3E";

  return (
    <section className="partners-section" ref={ref}>
      <div className="partner-section-wrapper">
        <GridPattern />
      <div className="container">
          <div className="section-header">
            <div className="section-label">Our Partners</div>
            <h2 className="section-title-partners">Trusted by visionaries worldwide</h2>
          </div>

          <div className="partners-marquee-container">
            {/* Row 1: Right to Left - Logos 1-18 */}
            <div className="partners-scroll-container">
              <div className="partners-scroll-track scroll-right-to-left">
                {duplicatedRow1.map((partner, index) => (
                  <div key={`row1-${partner.id}-${index}`} className="partner-logo-wrapper">
                    <div className="partner-logo">
                      <img
                        src={partner.logo}
                        alt={`Partner ${partner.id}`}
                        loading="lazy"
                        decoding="async"
                        fetchPriority={index < 6 ? 'high' : 'auto'}
                        onError={(e) => {
                          if (!e.target.dataset.fallback) {
                            e.target.dataset.fallback = 'true';
                            e.target.src = placeholderSvg;
                          }
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Row 2: Left to Right - Logos 19-36 */}
            <div className="partners-scroll-container">
              <div className="partners-scroll-track scroll-left-to-right">
                {duplicatedRow2.map((partner, index) => (
                  <div key={`row2-${partner.id}-${index}`} className="partner-logo-wrapper">
                    <div className="partner-logo">
                      <img
                        src={partner.logo}
                        alt={`Partner ${partner.id}`}
                        loading="lazy"
                        decoding="async"
                        fetchPriority={index < 6 ? 'high' : 'auto'}
                        onError={(e) => {
                          if (!e.target.dataset.fallback) {
                            e.target.dataset.fallback = 'true';
                            e.target.src = placeholderSvg;
                          }
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Row 3: Right to Left - Logos 37-53 */}
            <div className="partners-scroll-container">
              <div className="partners-scroll-track scroll-right-to-left">
                {duplicatedRow3.map((partner, index) => (
                  <div key={`row3-${partner.id}-${index}`} className="partner-logo-wrapper">
                    <div className="partner-logo">
                      <img
                        src={partner.logo}
                        alt={`Partner ${partner.id}`}
                        loading="lazy"
                        decoding="async"
                        fetchPriority={index < 6 ? 'high' : 'auto'}
                        onError={(e) => {
                          if (!e.target.dataset.fallback) {
                            e.target.dataset.fallback = 'true';
                            e.target.src = placeholderSvg;
                          }
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;
